import React, { useEffect, useState, useMemo } from 'react';
import {
  FaWallet, FaRupeeSign, FaCalendarDay, FaMoneyBillWave,
  FaUniversity, FaBuilding, FaFilter, FaCalendarAlt
} from 'react-icons/fa';
import api from '../../../utils/api';
import useColleges from '../../../utils/useColleges';
import { useAuth } from '../../../context/AuthContext';
import './Dashboard.css';

/* ─── Helpers ─────────────────────────────────────────────── */
const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

const getTodayStr = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};
const getMonthStr = () => getTodayStr().slice(0, 7);
const getYearStr  = () => getTodayStr().slice(0, 4);

// Financial year: 1 Apr YYYY to 31 Mar YYYY+1
// e.g. "2024-25" → { from: '2024-04-01', to: '2025-03-31' }
const getCurrentFY = () => {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-${String(year + 1).slice(2)}`;
};

const getFYRange = (fy) => {
  if (!fy) return null;
  const [startYear] = fy.split('-').map(Number);
  return {
    fromStr: `${startYear}-04-01`,
    toStr:   `${startYear + 1}-03-31`,
  };
};

// Get local date string YYYY-MM-DD from any date (ignores UTC offset)
const toLocalDateStr = (dateVal) => {
  if (!dateVal) return null;
  const d = new Date(dateVal);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const dateToFY = (dateStr) => {
  if (!dateStr) return null;
  const local = toLocalDateStr(dateStr);
  const [y, m] = local.split('-').map(Number);
  const startYear = m >= 4 ? y : y - 1;
  return `${startYear}-${String(startYear + 1).slice(2)}`;
};

const getAvailableFYs = (allocations, expenses) => {
  const set = new Set();
  allocations.forEach(a =>
    (a.transactions || []).forEach(txn => {
      // Use each paymentMode's own date for FY detection
      (txn.paymentModes || []).forEach(m => {
        const fy = dateToFY(m.date || txn.paidAt);
        if (fy) set.add(fy);
      });
      if (!(txn.paymentModes || []).length) {
        const fy = dateToFY(txn.paidAt);
        if (fy) set.add(fy);
      }
    })
  );
  expenses.forEach(ex => {
    const fy = dateToFY(ex.expenseDate || ex.date || ex.createdAt);
    if (fy) set.add(fy);
  });
  const result = Array.from(set).sort((a, b) => b.localeCompare(a));
  return result.length ? result : [getCurrentFY()];
};

const calcSplit = (item, amtField = 'amount', modeField = 'paymentMode') => {
  const amt  = Number(item[amtField]) || 0;
  const mode = (item[modeField] || 'Cash').toLowerCase();
  return mode === 'cash' ? { cash: amt, online: 0 } : { cash: 0, online: amt };
};

/* ─── KpiCard ──────────────────────────────────────────────── */
const KpiCard = ({ title, icon, total, cash, online, isExpense = false }) => (
  <div className={`dash-kpi-card${isExpense ? ' expense-card' : ''}`}>
    <div className="dash-kpi-header">
      <div className="dash-kpi-icon">{icon}</div>
      <h3 className="dash-kpi-title">{title}</h3>
    </div>
    <div className="dash-kpi-body">
      <div className="dash-kpi-total">{fmt(total)}</div>
      {(cash > 0 || online > 0) && (
        <div className="dash-kpi-splits">
          {cash   > 0 && <span className="split-badge cash">Cash: {fmt(cash)}</span>}
          {online > 0 && <span className="split-badge online">Online: {fmt(online)}</span>}
        </div>
      )}
    </div>
  </div>
);

/* ─── Expense Viewer ───────────────────────────────────────── */
const CollegeExpenseViewer = ({ expenses, defaultFY, fyOptions }) => {
  const [tab, setTab]                 = useState(defaultFY ? 'fy' : 'all');
  const [dateFilter, setDateFilter]   = useState(getTodayStr());
  const [monthFilter, setMonthFilter] = useState(getMonthStr());
  const [yearFilter, setYearFilter]   = useState(getYearStr());
  const [fyFilter, setFyFilter]       = useState(defaultFY || getCurrentFY());
  const [headFilter, setHeadFilter]   = useState('all');

  // sync tab & fyFilter when parent FY changes
  useEffect(() => {
    if (defaultFY) { setTab('fy'); setFyFilter(defaultFY); }
    else setTab('all');
  }, [defaultFY]);

  const headOptions = useMemo(() => {
    const set = new Set();
    expenses.forEach(ex => set.add(ex.head?.name || 'Other Expense'));
    return Array.from(set).sort();
  }, [expenses]);

  const filtered = useMemo(() => {
    return expenses.filter(ex => {
      const exDate = ex.expenseDate || ex.date || ex.createdAt;
      if (!exDate) return false;
      const dStr = new Date(exDate).toISOString();
      const fyRange   = tab === 'fy' ? getFYRange(fyFilter) : null;
      const timeMatch =
        tab === 'date'  ? dStr.startsWith(dateFilter)  :
        tab === 'month' ? dStr.startsWith(monthFilter) :
        tab === 'year'  ? dStr.startsWith(yearFilter)  :
        tab === 'fy'    ? (fyRange ? toLocalDateStr(exDate) >= fyRange.fromStr && toLocalDateStr(exDate) <= fyRange.toStr : true) :
        true;
      const headMatch = headFilter === 'all' || (ex.head?.name || 'Other Expense') === headFilter;
      return timeMatch && headMatch;
    });
  }, [expenses, tab, dateFilter, monthFilter, yearFilter, fyFilter, headFilter]);

  const aggregated = useMemo(() => {
    const map = {};
    let totalCash = 0, totalOnline = 0;
    filtered.forEach(ex => {
      const head = ex.head?.name || 'Other Expense';
      if (!map[head]) map[head] = { name: head, total: 0, cash: 0, online: 0 };
      const { cash, online } = calcSplit(ex, 'amount', 'paymentMode');
      map[head].total += cash + online;
      map[head].cash  += cash;
      map[head].online += online;
      totalCash   += cash;
      totalOnline += online;
    });
    return {
      list: Object.values(map).sort((a, b) => b.total - a.total),
      totalCash, totalOnline,
      grandTotal: totalCash + totalOnline,
    };
  }, [filtered]);

  return (
    <div className="dash-exp-viewer">
      <div className="dash-exp-controls">
        <div className="dash-exp-tabs">
          <button className={tab === 'all'   ? 'active' : ''} onClick={() => setTab('all')}>All Time</button>
          <button className={tab === 'fy'    ? 'active' : ''} onClick={() => setTab('fy')}>By FY</button>
          <button className={tab === 'date'  ? 'active' : ''} onClick={() => setTab('date')}>By Date</button>
          <button className={tab === 'month' ? 'active' : ''} onClick={() => setTab('month')}>By Month</button>
          <button className={tab === 'year'  ? 'active' : ''} onClick={() => setTab('year')}>By Year</button>
        </div>
        <div className="dash-exp-filters">
          {tab === 'fy'    && (
            <select value={fyFilter} onChange={e => setFyFilter(e.target.value)}>
              {fyOptions.map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
            </select>
          )}
          {tab === 'date'  && <input type="date"  value={dateFilter}  onChange={e => setDateFilter(e.target.value)} />}
          {tab === 'month' && <input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} />}
          {tab === 'year'  && (
            <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
              {[0,1,2,3].map(i => { const y = String(new Date().getFullYear() - i); return <option key={y} value={y}>{y}</option>; })}
            </select>
          )}
          <select className="dash-exp-head-sel" value={headFilter} onChange={e => setHeadFilter(e.target.value)}>
            <option value="all">All Heads</option>
            {headOptions.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
      </div>

      <div className="dash-exp-summary">
        <span className="exp-grand-total">Filtered Total: <strong>{fmt(aggregated.grandTotal)}</strong></span>
        <div className="dash-kpi-splits">
          {aggregated.totalCash   > 0 && <span className="split-badge cash">Cash: {fmt(aggregated.totalCash)}</span>}
          {aggregated.totalOnline > 0 && <span className="split-badge online">Online: {fmt(aggregated.totalOnline)}</span>}
        </div>
      </div>

      {aggregated.list.length > 0 ? (
        <div className="dash-exp-list">
          {aggregated.list.map((item, idx) => (
            <div key={idx} className="dash-exp-row">
              <span className="exp-head-name">{item.name}</span>
              <div className="exp-head-stats">
                <div className="dash-kpi-splits">
                  {item.cash   > 0 && <span className="split-badge cash sm">Cash: {fmt(item.cash)}</span>}
                  {item.online > 0 && <span className="split-badge online sm">Online: {fmt(item.online)}</span>}
                </div>
                <span className="exp-head-total">{fmt(item.total)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="dash-empty">No expenses found for this selection.</div>
      )}
    </div>
  );
};

/* ─── Shared Financial Dashboard ─────────────────────────────
   Reads role from useAuth():
   - superadmin / admin  → sees all colleges, college filter shown
   - collegeadmin / clerk → locked to their own college, no filter
──────────────────────────────────────────────────────────────── */
const Dashboard = () => {
  const { user } = useAuth();
  const role = user?.role || 'admin';
  const isLocked = role === 'collegeadmin' || role === 'clerk';
  const lockedCollegeId = isLocked ? String(user?.college?._id || user?.college || '') : null;

  const TITLES = { superadmin: 'Super Admin Dashboard', admin: 'Dashboard', collegeadmin: 'College Dashboard', clerk: 'Clerk Dashboard' };
  const title  = TITLES[role] || 'Dashboard';
  const { colleges: allColleges, loading: collegesLoading } = useColleges();
  const [loading, setLoading]             = useState(true);
  const [filterCollege, setFilterCollege] = useState(lockedCollegeId || '');
  const [filterSession, setFilterSession] = useState('');
  const [filterFY, setFilterFY]           = useState(getCurrentFY());
  const [sessions, setSessions]           = useState([]);
  const [allocations, setAllocations]     = useState([]);
  const [expenses, setExpenses]           = useState([]);

  const fyOptions = useMemo(() => getAvailableFYs(allocations, expenses), [allocations, expenses]);

  // When lockedCollegeId changes (e.g. on mount), keep filterCollege in sync
  useEffect(() => {
    if (lockedCollegeId) setFilterCollege(lockedCollegeId);
  }, [lockedCollegeId]);

  useEffect(() => {
    Promise.allSettled([
      api.get('/sessions'),
      api.get('/fees/allocations?limit=20000'),
      api.get('/expenses?limit=10000'),
    ]).then(([sess, alloc, exp]) => {
      if (sess.status  === 'fulfilled') setSessions(sess.value.data.data || []);
      if (alloc.status === 'fulfilled') setAllocations(alloc.value.data.data || []);
      if (exp.status   === 'fulfilled') setExpenses(exp.value.data.data || []);
      setLoading(false);
    });
  }, []);

  // Colleges visible in the filter dropdown (all for admin/superadmin, just own for others)
  const colleges = useMemo(() => {
    if (!lockedCollegeId) return allColleges;
    return allColleges.filter(c => String(c._id) === lockedCollegeId);
  }, [allColleges, lockedCollegeId]);

  const sessionOptions = useMemo(() => {
    const set = new Set();
    sessions.forEach(s => {
      const cid = String(s.college?._id || s.college || '');
      if (!filterCollege || cid === filterCollege) set.add(s.name);
    });
    return Array.from(set).sort();
  }, [sessions, filterCollege]);

  /* ─── Per-college aggregation ─── */
  const collegeStats = useMemo(() => {
    const todayStr = getTodayStr();
    const fyRange  = getFYRange(filterFY);

    const inFY = (dateVal) => {
      if (!fyRange || !dateVal) return true;
      const local = toLocalDateStr(dateVal);
      return local >= fyRange.fromStr && local <= fyRange.toStr;
    };

    return colleges.map(c => {
      const cid = String(c._id);
      const isMatch = (item) => {
        const itemSess = item.session?.name || item.session || '';
        return !filterSession || itemSess === filterSession;
      };

      const colAllocs = allocations.filter(
        a => String(a.college?._id || a.college) === cid && isMatch(a)
      );

      let totalColl = 0, totalCollCash = 0, totalCollOnline = 0;
      let totalDue  = 0;
      let todayColl = 0, todayCollCash = 0, todayCollOnline = 0;

      colAllocs.forEach(a => {
        totalDue += Number(a.dueAmount || 0);

        (a.transactions || []).forEach(txn => {
          if (!txn.paidAmount || txn.paidAmount <= 0) return;
          (txn.paymentModes || []).forEach(m => {
            const amt      = Number(m.amount) || 0;
            if (!amt) return;
            const mDate    = m.date || txn.paidAt;  // use mode's own date
            const isCash   = (m.mode || 'Cash').toLowerCase() === 'cash';
            const isInFY   = inFY(mDate);
            const isToday  = mDate && toLocalDateStr(mDate) === todayStr;
            if (isInFY) {
              if (isCash) { totalColl += amt; totalCollCash   += amt; }
              else        { totalColl += amt; totalCollOnline += amt; }
            }
            if (isToday) {
              if (isCash) { todayColl += amt; todayCollCash   += amt; }
              else        { todayColl += amt; todayCollOnline += amt; }
            }
          });
        });
      });

      const colExp = expenses.filter(
        ex => String(ex.college?._id || ex.college) === cid && isMatch(ex)
      );
      let totalExp = 0, totalExpCash = 0, totalExpOnline = 0;
      let todayExp = 0, todayExpCash = 0, todayExpOnline = 0;

      colExp.forEach(ex => {
        const exDate = ex.expenseDate || ex.date || ex.createdAt;
        const { cash, online } = calcSplit(ex, 'amount', 'paymentMode');
        if (inFY(exDate)) {
          totalExp += cash + online; totalExpCash += cash; totalExpOnline += online;
        }
        if (exDate && toLocalDateStr(exDate) === todayStr) {
          todayExp += cash + online; todayExpCash += cash; todayExpOnline += online;
        }
      });

      return {
        _id: cid, name: c.name,
        totalColl, totalCollCash, totalCollOnline,
        totalDue,
        todayColl, todayCollCash, todayCollOnline,
        totalExp, totalExpCash, totalExpOnline,
        todayExp, todayExpCash, todayExpOnline,
        colExp,
      };
    });
  }, [colleges, allocations, expenses, filterSession, filterFY]);

  const visibleColleges = useMemo(() =>
    filterCollege ? collegeStats.filter(c => c._id === filterCollege) : collegeStats,
  [collegeStats, filterCollege]);

  const overview = useMemo(() => {
    const g = {
      totalColl: 0, totalCollCash: 0, totalCollOnline: 0, totalDue: 0,
      todayColl: 0, todayCollCash: 0, todayCollOnline: 0,
      totalExp: 0, totalExpCash: 0, totalExpOnline: 0,
      todayExp: 0, todayExpCash: 0, todayExpOnline: 0,
    };
    visibleColleges.forEach(c => {
      g.totalColl     += c.totalColl;     g.totalCollCash  += c.totalCollCash;  g.totalCollOnline += c.totalCollOnline;
      g.totalDue      += c.totalDue;
      g.todayColl     += c.todayColl;     g.todayCollCash  += c.todayCollCash;  g.todayCollOnline += c.todayCollOnline;
      g.totalExp      += c.totalExp;      g.totalExpCash   += c.totalExpCash;   g.totalExpOnline  += c.totalExpOnline;
      g.todayExp      += c.todayExp;      g.todayExpCash   += c.todayExpCash;   g.todayExpOnline  += c.todayExpOnline;
    });
    return g;
  }, [visibleColleges]);

  if (loading || collegesLoading) return (
    <div className="dash-loading">
      <div className="spinner" />
      <p>Aggregating Dashboard Data...</p>
    </div>
  );

  const renderKpiGrid = (data) => (
    <div className="dash-kpi-grid">
      <KpiCard title={filterFY ? `Total Collection (FY ${filterFY})` : 'Total Collection'} icon={<FaWallet />}        total={data.totalColl} cash={data.totalCollCash} online={data.totalCollOnline} />
      <KpiCard title="Total Fee Due"      icon={<FaRupeeSign />}     total={data.totalDue} />
      <KpiCard title="Today's Collection" icon={<FaCalendarDay />}   total={data.todayColl} cash={data.todayCollCash} online={data.todayCollOnline} />
      <KpiCard title={filterFY ? `Total Expense (FY ${filterFY})` : 'Total Expense'}      icon={<FaMoneyBillWave />} total={data.totalExp} cash={data.totalExpCash} online={data.totalExpOnline} isExpense />
      <KpiCard title="Today's Expense"    icon={<FaMoneyBillWave />} total={data.todayExp} cash={data.todayExpCash} online={data.todayExpOnline} isExpense />
    </div>
  );

  const showCollegeFilter = !lockedCollegeId;

  return (
    <div className="dash-wrapper">

      {/* ── HEADER ── */}
      <div className="dash-header">
        <div className="dash-header-left">
          <h1>{title}</h1>
          {showCollegeFilter && (
            <span className="dash-badge bg-blue"><FaBuilding /> {colleges.length} Colleges</span>
          )}
        </div>
        <div className="dash-header-right">
          {showCollegeFilter && (
            <div className="dash-filter">
              <FaFilter />
              <select value={filterCollege} onChange={e => { setFilterCollege(e.target.value); setFilterSession(''); }}>
                <option value="">All Colleges</option>
                {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="dash-filter">
            <FaCalendarAlt />
            <select value={filterFY} onChange={e => setFilterFY(e.target.value)}>
              <option value="">All Time</option>
              {fyOptions.map(fy => (
                <option key={fy} value={fy}>FY {fy}</option>
              ))}
            </select>
          </div>
          <div className="dash-filter">
            <FaCalendarAlt />
            <select value={filterSession} onChange={e => setFilterSession(e.target.value)} disabled={!sessionOptions.length}>
              <option value="">All Sessions</option>
              {sessionOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── GLOBAL OVERVIEW — only when no specific college is selected ── */}
      {!filterCollege && (
        <div className="dash-section">
          <h2 className="dash-section-title">Global Overview</h2>
          {renderKpiGrid(overview)}
        </div>
      )}

      {/* ── COLLEGE CARDS ── */}
      <div className="dash-colleges-wrapper">
        {visibleColleges.map(college => (
          <div key={college._id} className="dash-college-card">
            <div className="dash-college-header">
              <FaUniversity className="c-icon" />
              <h2>{college.name}</h2>
            </div>
            <div className="dash-college-body">
              <h3 className="sub-title">Financial Stats</h3>
              {renderKpiGrid(college)}
              <h3 className="sub-title mt-4">Expense Details &amp; Breakdown</h3>
              <CollegeExpenseViewer expenses={college.colExp} defaultFY={filterFY || null} fyOptions={fyOptions} />
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default Dashboard;
